import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PROFILES_QUERY_KEYS } from '../constants';
import { profilesService } from '../services';
import type { ProfileInput } from '../types';

export function useProfiles(params: { page: number; search?: string; perPage?: number }) {
  return useQuery({
    queryKey: PROFILES_QUERY_KEYS.list(params.page, params.perPage ?? 20, params.search ?? ''),
    queryFn: () => profilesService.list(params),
    placeholderData: (previous) => previous,
  });
}

export function useProfile(id: string | undefined) {
  return useQuery({
    queryKey: PROFILES_QUERY_KEYS.detail(id ?? ''),
    queryFn: () => profilesService.get(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ProfileInput) => profilesService.create(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROFILES_QUERY_KEYS.all }),
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<ProfileInput> }) =>
      profilesService.update(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROFILES_QUERY_KEYS.all }),
  });
}

export function useDeleteProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => profilesService.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROFILES_QUERY_KEYS.all }),
  });
}
